import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ExternalLink, Plug, XCircle } from 'lucide-react';
import { presetForAddress, type MailAccountView, type ProviderPreset, type TestConnectionResult } from '@nlinks/shared';
import { api, ApiError } from '@/lib/api';
import { useProviderPresets } from '@/lib/queries';
import { Alert, Button, Field, Input, Select, Toggle } from '@/components/ui';

export interface MailboxFormValues {
  label: string;
  emailAddress: string;
  provider: string;
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  username: string;
  password: string;
  folder: string;
  syncEnabled: boolean;
  /** ISO date (yyyy-mm-dd). Mail older than this is never fetched. */
  syncFromDate: string;
}

/** Today, as yyyy-mm-dd in the browser's own timezone. */
function todayIso(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

const EMPTY: MailboxFormValues = {
  label: '',
  emailAddress: '',
  provider: 'CUSTOM',
  imapHost: '',
  imapPort: 993,
  imapSecure: true,
  username: '',
  password: '',
  folder: 'INBOX',
  syncEnabled: true,
  syncFromDate: todayIso(),
};

/**
 * Add/edit form for a mailbox.
 *
 * The "Test connection" button is the important part of this screen: the API
 * verifies credentials before it will save them, so letting the user find out
 * here — with the provider-specific hint visible — avoids a confusing rejection
 * on submit.
 */
export function MailboxForm({
  existing,
  onSubmit,
  onCancel,
  submitting,
  submitLabel = 'Add mailbox',
}: {
  existing?: MailAccountView;
  onSubmit: (values: MailboxFormValues) => Promise<void> | void;
  onCancel?: () => void;
  submitting?: boolean;
  submitLabel?: string;
}) {
  const { data: presets = [] } = useProviderPresets();

  const [values, setValues] = useState<MailboxFormValues>(() =>
    existing
      ? {
          label: existing.label,
          emailAddress: existing.emailAddress,
          provider: existing.provider,
          imapHost: existing.imapHost,
          imapPort: existing.imapPort,
          imapSecure: existing.imapSecure,
          username: existing.username,
          password: '',
          folder: existing.folder,
          syncEnabled: existing.syncEnabled,
          syncFromDate: existing.syncFromDate.slice(0, 10),
        }
      : EMPTY,
  );

  const [showAdvanced, setShowAdvanced] = useState(Boolean(existing));
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof MailboxFormValues>(key: K, value: MailboxFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setTestResult(null);
  };

  const activePreset: ProviderPreset | undefined = useMemo(
    () => presets.find((p) => p.id === values.provider),
    [presets, values.provider],
  );

  // Detect the provider from the address as the user types, and fill in the
  // IMAP host for them. Only while creating — never overwrite a saved config.
  useEffect(() => {
    if (existing || !values.emailAddress.includes('@')) return;
    const detected = presetForAddress(values.emailAddress);
    if (detected.id === 'CUSTOM' || detected.id === values.provider) return;

    setValues((prev) => ({
      ...prev,
      provider: detected.id,
      imapHost: detected.imapHost,
      imapPort: detected.imapPort,
      imapSecure: detected.imapSecure,
      label: prev.label || detected.label,
    }));
  }, [values.emailAddress, values.provider, existing]);

  const applyPreset = (id: string) => {
    const preset = presets.find((p) => p.id === id);
    setTestResult(null);
    setValues((prev) => ({
      ...prev,
      provider: id,
      ...(preset && preset.imapHost
        ? { imapHost: preset.imapHost, imapPort: preset.imapPort, imapSecure: preset.imapSecure }
        : {}),
    }));
  };

  const canTest =
    values.emailAddress.includes('@') && values.imapHost.length > 3 && values.password.length > 0;

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const result = await api.post<TestConnectionResult>('/api/mail-accounts/test', {
        emailAddress: values.emailAddress,
        imapHost: values.imapHost,
        imapPort: values.imapPort,
        imapSecure: values.imapSecure,
        username: values.username || values.emailAddress,
        password: values.password,
        folder: values.folder,
      });
      setTestResult(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the mail server.');
    } finally {
      setTesting(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save this mailbox.');
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      {error ? <Alert severity="error">{error}</Alert> : null}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Email address" required htmlFor="emailAddress">
          <Input
            id="emailAddress"
            type="email"
            value={values.emailAddress}
            onChange={(e) => set('emailAddress', e.target.value.trim())}
            placeholder="seed-04@example.com"
            disabled={Boolean(existing)}
            required
            autoComplete="off"
          />
        </Field>

        <Field label="Display name" required htmlFor="label" hint="How this mailbox appears in your dashboard.">
          <Input
            id="label"
            value={values.label}
            onChange={(e) => set('label', e.target.value)}
            placeholder="Campaign seed 04"
            required
          />
        </Field>
      </div>

      <Field label="Provider" htmlFor="provider">
        <Select id="provider" value={values.provider} onChange={(e) => applyPreset(e.target.value)}>
          {presets.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.label}
            </option>
          ))}
        </Select>
      </Field>

      {activePreset ? (
        <Alert severity="info">
          <p>{activePreset.credentialHint}</p>
          {activePreset.helpUrl ? (
            <a
              href={activePreset.helpUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-brand-300 hover:text-brand-200"
            >
              Generate an app password
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          ) : null}
        </Alert>
      ) : null}

      <Field
        label={existing ? 'App password (leave blank to keep the current one)' : 'App password'}
        required={!existing}
        htmlFor="password"
      >
        <Input
          id="password"
          type="password"
          value={values.password}
          onChange={(e) => set('password', e.target.value)}
          placeholder={existing ? '••••••••••••' : 'abcd efgh ijkl mnop'}
          required={!existing}
          autoComplete="new-password"
        />
      </Field>

      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="text-sm text-brand-400 transition hover:text-brand-300"
      >
        {showAdvanced ? 'Hide' : 'Show'} server settings
      </button>

      {showAdvanced ? (
        <div className="grid gap-4 rounded-lg border border-surface-border bg-surface-base/50 p-4 sm:grid-cols-2">
          <Field label="IMAP host" required htmlFor="imapHost">
            <Input
              id="imapHost"
              value={values.imapHost}
              onChange={(e) => set('imapHost', e.target.value.trim())}
              placeholder="imap.example.com"
              required
            />
          </Field>

          <Field label="Port" required htmlFor="imapPort">
            <Input
              id="imapPort"
              type="number"
              min={1}
              max={65535}
              value={values.imapPort}
              onChange={(e) => set('imapPort', Number(e.target.value))}
              required
            />
          </Field>

          <Field label="Username" htmlFor="username" hint="Defaults to the email address.">
            <Input
              id="username"
              value={values.username}
              onChange={(e) => set('username', e.target.value.trim())}
              placeholder={values.emailAddress || 'user@example.com'}
              autoComplete="off"
            />
          </Field>

          <Field label="Folder" htmlFor="folder" hint="INBOX unless mail is filtered elsewhere.">
            <Input id="folder" value={values.folder} onChange={(e) => set('folder', e.target.value)} required />
          </Field>

          <div className="sm:col-span-2">
            <Toggle
              checked={values.imapSecure}
              onChange={(v) => set('imapSecure', v)}
              label="Use TLS"
              description="Required on port 993. Turn off only for a STARTTLS server on port 143."
            />
          </div>
        </div>
      ) : null}

      <Field
        label="Start checking from"
        required
        htmlFor="syncFromDate"
        hint="Mail received before this date is ignored entirely. An established inbox can hold thousands of old messages — there is no point opening links from last year."
      >
        <Input
          id="syncFromDate"
          type="date"
          value={values.syncFromDate}
          onChange={(e) => set('syncFromDate', e.target.value)}
          max={todayIso()}
          required
        />
      </Field>

      <Toggle
        checked={values.syncEnabled}
        onChange={(v) => set('syncEnabled', v)}
        label="Sync this mailbox"
        description="When off, mail is not fetched and no links are visited."
      />

      {testResult ? (
        <Alert severity={testResult.ok ? 'success' : 'error'}>
          <div className="flex items-start gap-2">
            {testResult.ok ? (
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            ) : (
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            )}
            <span>{testResult.message}</span>
          </div>
        </Alert>
      ) : null}

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-surface-border pt-5">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          onClick={test}
          loading={testing}
          disabled={!canTest}
          icon={<Plug className="h-4 w-4" />}
        >
          Test connection
        </Button>
        <Button type="submit" loading={submitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
